import React, {
  Children,
  Component,
  createRef,
  forwardRef,
  ReactNode,
  Ref,
  RefObject,
} from "react";

import {
  isElementInViewport,
  visibleHeightOfElement,
} from "@at/window-utils/lib/viewport-utils/viewport.util";

import { WrapperDiv } from "./tabs-pattern.styled";
import { TabHeadingType, TabPanelType } from "./tabs-pattern.type";

type TabsRenderType = "tab" | "tabline";

type TypeRefList = HTMLUListElement;
type TypeRefHeading = HTMLLIElement;
type TypeRefTab = HTMLElement;

export interface TabsProps {
  activeTab?: number;
  anchorToHeadingOffset?: number;
  children?: ReactNode;
  componentIdentifier: string;
  customClassName?: string;
  headings: TabHeadingType[];
  panels?: TabPanelType[] | null | undefined;
  renderType?: TabsRenderType;
}

interface TabsState {
  activeTab: number;
}

const className = "tabs";

interface TabHeadingProps {
  activeTab: number;
  className: string;
  customClassName?: string;
  heading: TabHeadingType;
  index: number;
  pairingID: string;
  setActiveTab: (arg0: number) => void;
}

const TabHeading = forwardRef(
  (props: TabHeadingProps, ref: Ref<TypeRefHeading>) => {
    const {
      activeTab, //
      className,
      customClassName,
      heading,
      index,
      pairingID,
      setActiveTab,
    } = props;

    const {
      additionalProps = {}, //
      callback,
      displayValue,
      isDisabled,
    } = heading;

    const linkClassName = `${className}__link`;
    const isSelected = activeTab === index;
    const selectedClassName = isSelected ? ` ${className}--selected` : "";
    const disabledClassName = isDisabled ? ` ${className}--disabled` : "";
    const tabCustomClassName = customClassName ? ` ${customClassName}-tab` : "";

    return (
      <li
        ref={ref}
        className={`${className}${selectedClassName}${disabledClassName}${tabCustomClassName}`}
        role="presentation"
        id={`${pairingID}-tab`}
        {...additionalProps}
      >
        <button
          className={
            isDisabled
              ? `${linkClassName} ${linkClassName}--disabled`
              : linkClassName
          }
          type="button"
          role="tab"
          aria-controls={pairingID}
          disabled={isDisabled}
          aria-selected={isSelected}
          onClick={() => {
            setActiveTab(index);
            callback && callback();
          }}
        >
          {displayValue}
        </button>
      </li>
    );
  },
);

TabHeading.displayName = "TabHeading";

interface TabProps {
  activeTab: number;
  component: ReactNode;
  index: number;
  pairingID: string;
  panel?: TabPanelType;
}

const Tab = forwardRef((props: TabProps, ref: Ref<TypeRefTab>) => {
  const {
    activeTab, //
    component,
    index,
    pairingID,
    panel = {},
  } = props;

  const {
    additionalProps = {}, //
  } = panel;

  const tabClassName = `${className}--${
    activeTab === index ? "active" : "inactive"
  }${activeTab === index ? "" : " hidden"}`;

  return (
    <section
      ref={ref}
      id={pairingID}
      role="tabpanel"
      aria-labelledby={`${pairingID}-tab`}
      aria-hidden={activeTab !== index}
      className={tabClassName}
      data-testid={pairingID}
      {...additionalProps}
    >
      {component}
    </section>
  );
});

Tab.displayName = "Tab";

export class TabsPattern extends Component<TabsProps, TabsState> {
  public refList: RefObject<TypeRefList> = createRef();
  public refHeadings: RefObject<TypeRefHeading>[] = [];
  public refTabs: RefObject<TypeRefTab>[] = [];

  public constructor(props: TabsProps) {
    super(props);

    this.state = {
      activeTab: props.activeTab || 0,
    };

    this.setActiveTabBasedOnHash = this.setActiveTabBasedOnHash.bind(this);
  }

  public scrollTabHeadingIntoView = (activeTab: number): void => {
    if (this.refHeadings.length === 0) {
      return;
    }

    const { refList, refHeadings } = this;
    const heading = refList.current;

    const activeHeadingRef = refHeadings[activeTab];

    if (activeHeadingRef && heading && activeHeadingRef.current) {
      heading.scrollLeft = activeHeadingRef.current.offsetLeft;
    }

    this.scrollTabIntoView();
  };

  public scrollTabIntoView = (): void | null => {
    const { activeTab } = this.state;
    const { anchorToHeadingOffset = 82 } = this.props;
    const heading = this.refList.current;
    const tabReference = this.refTabs[activeTab];

    if (!(heading && tabReference)) {
      return null;
    }

    const tab = tabReference.current;

    if (!tab) {
      return null;
    }

    const minAmountOfTabVisible = 150;

    const shouldScrollToTab =
      minAmountOfTabVisible >= visibleHeightOfElement(tab) &&
      !isElementInViewport(tab);

    if (shouldScrollToTab) {
      window.scrollTo(0, heading.offsetTop - anchorToHeadingOffset);
    }
  };

  public setActiveTabBasedOnHash = () => {
    const { componentIdentifier } = this.props;

    const currentHash = self.location.hash || null;
    const restOfHash =
      currentHash === null
        ? null
        : currentHash.split(`${componentIdentifier}-`)[1];
    const activeTab = restOfHash ? Number(restOfHash.split("&")[0]) - 1 : null;

    if (activeTab === null) {
      return;
    }

    const isValidTab = this.refHeadings.length > activeTab && activeTab > -1;

    if (isValidTab && activeTab !== this.state.activeTab) {
      this.scrollTabHeadingIntoView(activeTab);
      this.setState({ activeTab });
    }
  };

  public componentDidUpdate(prevProps: TabsProps) {
    const { activeTab } = this.props;
    const state = this.state;

    if (activeTab === undefined) {
      return;
    }

    if (prevProps.activeTab !== activeTab && state.activeTab !== activeTab) {
      this.scrollTabHeadingIntoView(activeTab || 0);
      this.setState({ activeTab });
    }
  }

  public componentDidMount() {
    this.setActiveTabBasedOnHash();

    window.addEventListener("hashchange", this.setActiveTabBasedOnHash);
  }

  public componentWillUnmount() {
    window.removeEventListener("hashchange", this.setActiveTabBasedOnHash);
  }

  public render() {
    const {
      customClassName, //
      componentIdentifier,
      headings,
      panels,
      renderType = "tab",
      children,
    } = this.props;
    const { activeTab } = this.state;

    // eslint-disable-next-line unicorn/consistent-function-scoping
    const setActiveTab = (activeTab: number) => {
      this.scrollTabIntoView();
      this.setState({ activeTab });
    };

    const atcClassName = `atc-${renderType}`;

    const groupCustomClassName = customClassName
      ? ` ${customClassName}-tab-group`
      : "";

    const childComponents = Children.toArray(children).filter(
      (component) => !!component,
    );

    return (
      <WrapperDiv>
        <ul
          ref={this.refList}
          className={`${className} ${atcClassName}-group${groupCustomClassName}`}
          role="tablist"
        >
          {childComponents.map((component, index) => {
            const tabHeadingRef: RefObject<TypeRefHeading> = createRef();

            this.refHeadings.push(tabHeadingRef);

            // if (childComponents.length === 1) {
            //   return <> {headings[0]}</>;
            // }

            return (
              <TabHeading
                ref={this.refHeadings[index]}
                className={atcClassName}
                customClassName={customClassName}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                pairingID={`${componentIdentifier}-${index + 1}`}
                heading={headings[index]}
                index={index}
                key={index}
              />
            );
          })}
        </ul>

        {/** this is the info within the tab */}

        {childComponents.map((component, index) => {
          const tabRef: RefObject<TypeRefTab> = createRef();

          this.refTabs.push(tabRef);

          return (
            <Tab
              ref={this.refTabs[index]}
              activeTab={activeTab}
              pairingID={`${componentIdentifier}-${index + 1}`}
              panel={panels ? panels[index] : undefined}
              index={index}
              component={component}
              key={index}
            />
          );
        })}
      </WrapperDiv>
    );
  }
}
